/**
 * PRD.330 Phase 5 — Pure time-series bucketing.
 *
 * Grouping is derived ONLY from the applied canonical `query.dateRange`
 * (inclusive LOCAL calendar days) — never from preset identity:
 *   1 day      → null (the time series is not shown)
 *   2–45 days  → day
 *   46–180     → week (Monday-start calendar weeks)
 *   > 180      → month
 * Buckets are calendar periods clipped to the applied range, so a partial
 * first/last week or month only covers dates inside the range. Zero-fill
 * reconciles grouped query rows against this expected sequence.
 */
import type { ReportDateRange, ReportTimeBucket, ReportTimeSeriesPoint } from './report-types.js';

const DAY_MS = 86_400_000;

function parts(localDate: string): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!m) throw new Error(`Invalid local date: ${localDate}`);
  return [Number(m[1]), Number(m[2]) - 1, Number(m[3])];
}

/** Calendar-day ordinal (UTC arithmetic on the date parts — no DST effects). */
function ordinal(localDate: string): number {
  const [y, m, d] = parts(localDate);
  return Date.UTC(y, m, d) / DAY_MS;
}

function fromOrdinal(n: number): string {
  const d = new Date(n * DAY_MS);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Inclusive number of calendar days in the range (0 if inverted). */
export function inclusiveDayCount(range: ReportDateRange): number {
  const n = ordinal(range.to) - ordinal(range.from) + 1;
  return n > 0 ? n : 0;
}

/** The deterministic bucket for an applied range; null = not applicable. */
export function selectReportTimeBucket(range: ReportDateRange): ReportTimeBucket | null {
  const days = inclusiveDayCount(range);
  if (days <= 1) return null;
  if (days <= 45) return 'day';
  if (days <= 180) return 'week';
  return 'month';
}

export interface ReportTimeBucketRange {
  key: string;
  /** Inclusive local dates covered, clipped to the applied range. */
  from: string;
  to: string;
}

/** Expected calendar buckets for the range, in order, clipped to the range. */
export function buildReportTimeBuckets(
  range: ReportDateRange,
  bucket: ReportTimeBucket,
): ReportTimeBucketRange[] {
  const start = ordinal(range.from);
  const end = ordinal(range.to);
  if (end < start) return [];
  const out: ReportTimeBucketRange[] = [];
  let cursor = start;
  while (cursor <= end) {
    const date = new Date(cursor * DAY_MS);
    let periodStart: number;
    let periodEnd: number;
    let key: string;
    if (bucket === 'day') {
      periodStart = periodEnd = cursor;
      key = fromOrdinal(cursor);
    } else if (bucket === 'week') {
      const mondayOffset = (date.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
      periodStart = cursor - mondayOffset;
      periodEnd = periodStart + 6;
      key = fromOrdinal(periodStart);
    } else {
      const y = date.getUTCFullYear();
      const m = date.getUTCMonth();
      periodStart = Date.UTC(y, m, 1) / DAY_MS;
      periodEnd = Date.UTC(y, m + 1, 0) / DAY_MS;
      key = `${y}-${String(m + 1).padStart(2, '0')}`;
    }
    const clippedEnd = Math.min(periodEnd, end);
    out.push({ key, from: fromOrdinal(Math.max(periodStart, start)), to: fromOrdinal(clippedEnd) });
    cursor = clippedEnd + 1;
  }
  return out;
}

export interface ReportTimeSeriesRow {
  bucket: string | null;
  primary: number;
  secondary?: number;
}

/**
 * Zero-fill: every expected bucket appears (missing → 0), in calendar order.
 * Rows whose key is not an expected bucket are ignored — buckets are never
 * manufactured outside the applied range.
 */
export function fillReportTimeSeries(
  expected: ReadonlyArray<ReportTimeBucketRange>,
  rows: ReadonlyArray<ReportTimeSeriesRow>,
  withSecondary: boolean,
): ReportTimeSeriesPoint[] {
  const byKey = new Map<string, ReportTimeSeriesRow>();
  for (const row of rows) if (row.bucket !== null) byKey.set(row.bucket, row);
  return expected.map((b) => {
    const row = byKey.get(b.key);
    const point: ReportTimeSeriesPoint = {
      key: b.key,
      from: b.from,
      to: b.to,
      primary: row ? Number(row.primary) || 0 : 0,
    };
    if (withSecondary) point.secondary = row ? Number(row.secondary) || 0 : 0;
    return point;
  });
}
